import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Join from "@/pages/Join";
import DemoLanding from "@/pages/demo/DemoLanding";
import DemoDashboard from "@/pages/demo/DemoDashboard";
import DemoJobListing from "@/pages/demo/DemoJobListing";
import DemoProfile from "@/pages/demo/DemoProfile";
import DemoSent from "@/pages/demo/DemoSent";
import DemoAtsCompare from "@/pages/demo/DemoAtsCompare";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import ApplicationReview from "@/pages/ApplicationReview";
import SentApplications from "@/pages/SentApplications";
import Profile from "@/pages/Profile";
import Subscription from "@/pages/Subscription";
import PublicPortfolio from "@/pages/PublicPortfolio";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Unsubscribe from "@/pages/Unsubscribe";
import FloatingTools from "@/components/FloatingTools";
import ComingSoonGate from "@/components/ComingSoonGate";

// Pre-launch holding page. Set to false the moment you're ready for public traffic.
const COMING_SOON_ENABLED = true;
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div>;
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  return children;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

/**
 * Router root: short-circuits to <AuthCallback /> when the URL hash contains
 * an Emergent OAuth session_id. This runs synchronously during render so the
 * normal <Routes> + <Protected> tree never gets a chance to redirect the user
 * to /auth before the session has been exchanged.
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/join" element={<Join />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/demo/dashboard" element={<DemoDashboard />} />
      <Route path="/demo/profile" element={<DemoProfile />} />
      <Route path="/demo/sent" element={<DemoSent />} />
      <Route path="/demo/applications/:jobId" element={<DemoJobListing />} />
      <Route path="/demo/applications/:jobId/ats" element={<DemoAtsCompare />} />
      <Route path="/p/:slug" element={<PublicPortfolio />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/applications/:jobId" element={<Protected><ApplicationReview /></Protected>} />
      <Route path="/sent" element={<Protected><SentApplications /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.title = "Sisyphean · For creative professionals";
  }, []);

  const inner = (
    <BrowserRouter>
      <AppRouter />
      <FloatingTools />
    </BrowserRouter>
  );

  return (
    <ThemeProvider>
      <AuthProvider>
        {COMING_SOON_ENABLED ? <ComingSoonGate>{inner}</ComingSoonGate> : inner}
        <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: 'IBM Plex Mono, monospace', borderRadius: 0, fontSize: '12px' } }} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
